// Manutenção preventiva (pedido do titular, 08/10/2026): o plano de cada
// caminhão (a lista pronta e outros itens), o que vence pelo km ou pelo
// tempo, e os avisos do Início (também no app do motorista). O item feito
// entra pela despesa de manutenção do caminhão, com o km do painel.
import { categorias, despesas, manutencao, veiculos } from '../dados.js';
import { h, icone, trocar } from '../lib/dom.js';
import { mensagemDeErro } from '../lib/erros.js';
import { data, lerInteiro, numeroBR, plural } from '../lib/formato.js';
import {
  CATALOGO, avisosDaFrota, faltamNoPlano, kmAtual, planoDoVeiculo, textoDaProxima, textoDaSituacao, textoDaUltima, textoDoIntervalo,
} from '../lib/manutencao.js';
import { nestaTela } from '../rotas.js';
import { contaGravavel, hojeDaEmpresa, podeGravar } from '../sessao.js';
import { casca } from '../ui/casca.js';
import {
  avisar, campo, carregando, cartaoLink, confirmar, emblema, faixa, formulario, secao, vazio,
} from '../ui/componentes.js';
import { faixaDaConta } from './inicio.js';
import { textoCaminhao } from './viagens.js';

const EMBLEMA = {
  vencida: () => emblema('Vencida', 'perigo'),
  perto: () => emblema('Perto de vencer', 'alerta'),
  sem_base: () => emblema('Falta a última vez', 'alerta'),
};

async function dadosDaFrota() {
  const [frota, itens, leituras] = await Promise.all([veiculos.listar(), manutencao.listar(), despesas.leituras()]);
  return { frota, itens, leituras, hoje: hojeDaEmpresa() };
}

// Os avisos da frota inteira (resumo da semana).
export async function avisosDaManutencao() {
  const { frota, itens, leituras, hoje } = await dadosDaFrota();
  return avisosDaFrota({ veiculos: frota.filter((v) => v.tipo === 'trator'), itens, leituras, hoje });
}

// ---------------------------------------------------------------------
// Avisos (Início do escritório e do motorista)
// ---------------------------------------------------------------------
const linhaDoAviso = (a, { comPlaca = true } = {}) =>
  `${comPlaca ? `${textoCaminhao(a.veiculo)} · ` : ''}${a.item.nome}: ${textoDaSituacao(a.situacao)}`;

// O cartão do Início: o que venceu e o que está perto de vencer na frota.
export async function cartaoDaManutencao() {
  const { frota, itens, leituras, hoje } = await dadosDaFrota();
  const avisos = avisosDaFrota({ veiculos: frota.filter((v) => v.tipo === 'trator'), itens, leituras, hoje });
  if (avisos.length === 0) return null;
  const vencidas = avisos.filter((a) => a.situacao.estado === 'vencida').length;
  return h('a', { class: `cartao a-manutencao${vencidas > 0 ? ' vencida' : ''}`, href: '#/manutencao' },
    icone('chave', { tamanho: 28 }),
    h('span', { class: 'cartao-corpo' },
      h('span', { class: 'cartao-titulo' }, 'Manutenção'),
      avisos.slice(0, 3).map((a) => h('span', { class: 'cartao-linha' }, linhaDoAviso(a))),
      avisos.length > 3 && h('span', { class: 'cartao-linha' }, `e mais ${plural(avisos.length - 3, 'item', 'itens')}`)),
    icone('seta', { classe: 'icone seta' }));
}

// No app do motorista: os avisos dos caminhões das viagens dele (as em
// andamento; sem nenhuma, o da viagem mais recente).
export async function avisosDoMotorista(minhasViagens) {
  const abertas = minhasViagens.filter((v) => v.status === 'aberta');
  const recente = [...minhasViagens].filter((v) => v.status !== 'cancelada').sort((a, b) => (a.data_saida < b.data_saida ? 1 : -1))[0];
  const ids = new Set((abertas.length > 0 ? abertas : recente ? [recente] : []).map((v) => v.veiculo_id));
  if (ids.size === 0) return null;
  const { frota, itens, leituras, hoje } = await dadosDaFrota();
  const avisos = avisosDaFrota({ veiculos: frota.filter((v) => ids.has(v.id)), itens, leituras, hoje });
  if (avisos.length === 0) return null;
  const vencidas = avisos.some((a) => a.situacao.estado === 'vencida');
  return faixa(
    [h('span', null, 'Avise o escritório para marcar.'),
      h('ul', { class: 'avisos-manutencao' }, avisos.map((a) => h('li', null, linhaDoAviso(a, { comPlaca: ids.size > 1 }))))],
    vencidas ? 'perigo' : 'alerta',
    ids.size === 1 ? `Manutenção do caminhão ${textoCaminhao(avisos[0].veiculo)}` : 'Manutenção dos caminhões');
}

// ---------------------------------------------------------------------
// A frota: cada caminhão e o que pede atenção
// ---------------------------------------------------------------------
export function telaManutencao() {
  const corpo = h('div', { class: 'secao' }, carregando());
  (async () => {
    const { frota, itens, leituras, hoje } = await dadosDaFrota();
    const tratores = frota.filter((v) => v.tipo === 'trator' && v.ativo);
    if (tratores.length === 0) {
      trocar(corpo, vazio({ simbolo: 'chave', titulo: 'Nenhum caminhão ativo', texto: 'Cadastre os caminhões em Veículos.' }));
      return;
    }
    trocar(corpo,
      h('p', { class: 'pequeno' }, 'Cada item vence pelo km ou pelo tempo, o que chegar primeiro. Quando fizer, lance a despesa de manutenção do caminhão e marque o que foi feito, com o km do painel.'),
      h('div', { class: 'lista' }, tratores.map((v) => {
        const plano = planoDoVeiculo(v, { itens, leituras, hoje });
        const conta = (estado) => plano.filter((p) => p.situacao.estado === estado).length;
        const atencao = plano.filter((p) => p.situacao.estado !== 'ok');
        return cartaoLink({
          href: `#/veiculos/${v.id}/manutencao`,
          simbolo: 'chave',
          titulo: textoCaminhao(v),
          linhas: plano.length === 0
            ? ['Ainda sem plano de manutenção']
            : [
              atencao.length === 0 ? `${plural(plano.length, 'item', 'itens')} no plano, tudo em dia` : null,
              ...atencao.slice(0, 3).map((p) => `${p.item.nome}: ${textoDaSituacao(p.situacao)}`),
              atencao.length > 3 ? `e mais ${plural(atencao.length - 3, 'item', 'itens')}` : null,
            ],
          emblemas: [conta('vencida') > 0 && emblema(plural(conta('vencida'), 'vencida', 'vencidas'), 'perigo'),
            conta('perto') > 0 && emblema(`${conta('perto')} perto`, 'alerta')],
        });
      })));
  })().catch((erro) => {
    console.error(erro);
    trocar(corpo, faixa(mensagemDeErro(erro), 'perigo'));
  });
  return casca({ titulo: 'Manutenção preventiva', voltar: '/mais', conteudo: [corpo] });
}

// ---------------------------------------------------------------------
// O plano de um caminhão
// ---------------------------------------------------------------------
export function telaManutencaoVeiculo({ params }) {
  const tela = nestaTela();
  const corpo = h('div', { class: 'secao' }, carregando());
  const titulo = h('span', null, 'Manutenção');
  (async () => {
    const { frota, itens, leituras, hoje } = await dadosDaFrota();
    const v = frota.find((x) => x.id === params.id);
    if (!v) {
      trocar(corpo, vazio({ titulo: 'Caminhão não encontrado', acao: h('a', { class: 'botao', href: '#/veiculos' }, 'Ver veículos') }));
      return;
    }
    titulo.textContent = `Manutenção · ${textoCaminhao(v)}`;
    const gravavel = podeGravar() && v.ativo;
    const doVeiculo = itens.filter((i) => i.veiculo_id === v.id);
    const atual = kmAtual(v, leituras, doVeiculo);
    const plano = planoDoVeiculo(v, { itens, leituras, hoje });
    const cats = await categorias.listar();
    const catManutencao = cats.find((c) => c.codigo === 'manutencao');
    const linkDespesa = `#/despesas/nova?veiculo=${v.id}${catManutencao ? `&categoria=${catManutencao.id}` : ''}&volta=${encodeURIComponent(`/veiculos/${v.id}/manutencao`)}`;

    async function montar() {
      try {
        await manutencao.montar(CATALOGO.map((c) => ({
          veiculo_id: v.id, codigo: c.codigo, nome: c.nome, intervalo_km: c.km, intervalo_meses: c.meses,
          base_data: hoje, base_km: atual?.km ?? null,
        })));
        avisar('Plano montado. Se alguma troca foi feita antes, toque no item e informe quando foi.');
        tela.renderizar();
      } catch (erro) {
        avisar(mensagemDeErro(erro), 'erro');
      }
    }

    trocar(corpo,
      h('dl', { class: 'linha-dados resumo' },
        h('dt', null, 'Km do caminhão'),
        h('dd', null, atual ? `${numeroBR(atual.km)}${atual.data ? ` (${data(atual.data)})` : ''}` : 'Ainda sem km lançado')),
      !atual && h('p', { class: 'pequeno' }, 'Até o primeiro abastecimento com o km do painel, os itens contam só pelo tempo.'),
      plano.length === 0
        ? vazio({
          simbolo: 'chave',
          titulo: 'Ainda sem plano de manutenção',
          texto: 'Use a lista pronta (óleo do motor e filtros, câmbio, diferencial, filtros, graxa, freios, rodízio dos pneus, revisão, tacógrafo e extintor) e ajuste os intervalos depois. Cada item vence pelo km ou pelo tempo, o que chegar primeiro.',
          acao: gravavel && h('button', { class: 'botao cheio', type: 'button', onClick: (e) => { e.currentTarget.disabled = true; montar(); } }, 'Montar o plano com a lista pronta'),
        })
        : [
          h('p', { class: 'pequeno' }, 'Se alguma troca foi feita antes de montar o plano, toque no item e informe a data e o km da última vez.'),
          h('div', { class: 'lista' }, plano.map(({ item, situacao }) => cartaoLink({
            href: `#/manutencao/${item.id}`,
            simbolo: 'chave',
            titulo: item.nome,
            linhas: [textoDoIntervalo(item), textoDaUltima(situacao.ultima), textoDaProxima(situacao), situacao.estado !== 'ok' && textoDaSituacao(situacao)],
            emblemas: [EMBLEMA[situacao.estado]?.()],
          }))),
        ],
      gravavel && h('div', { class: 'acoes-lado' },
        h('a', { class: 'botao secundario', href: `#/veiculos/${v.id}/manutencao/novo` }, '+ Item'),
        plano.length > 0 && h('a', { class: 'botao secundario', href: linkDespesa }, '+ Despesa de manutenção')));
  })().catch((erro) => {
    console.error(erro);
    trocar(corpo, faixa(mensagemDeErro(erro), 'perigo'));
  });
  return casca({ titulo, voltar: `/veiculos/${params.id}`, conteudo: [contaGravavel() ? null : faixaDaConta(), corpo] });
}

// A seção da tela do caminhão: o resumo do plano e o link.
export async function blocoManutencaoDoVeiculo(v) {
  const { itens, leituras, hoje } = await dadosDaFrota();
  const plano = planoDoVeiculo(v, { itens, leituras, hoje });
  const atencao = plano.filter((p) => p.situacao.estado !== 'ok');
  const conta = (estado) => plano.filter((p) => p.situacao.estado === estado).length;
  return secao('Manutenção preventiva', h('div', { class: 'lista' },
    cartaoLink({
      href: `#/veiculos/${v.id}/manutencao`,
      simbolo: 'chave',
      titulo: plano.length === 0 ? 'Montar o plano de manutenção' : 'Ver o plano de manutenção',
      linhas: plano.length === 0
        ? ['Óleo, filtros, graxa, freios…: o app avisa pelo km ou pelo tempo']
        : [
          atencao.length === 0 ? `${plural(plano.length, 'item', 'itens')}, tudo em dia` : null,
          ...atencao.slice(0, 2).map((p) => `${p.item.nome}: ${textoDaSituacao(p.situacao)}`),
          atencao.length > 2 ? `e mais ${plural(atencao.length - 2, 'item', 'itens')}` : null,
        ],
      emblemas: [conta('vencida') > 0 && emblema(plural(conta('vencida'), 'vencida', 'vencidas'), 'perigo'),
        conta('perto') > 0 && emblema(`${conta('perto')} perto`, 'alerta')],
    })));
}

// ---------------------------------------------------------------------
// Um item do plano (novo ou alterar)
// ---------------------------------------------------------------------
export function telaItemManutencao({ params }) {
  const tela = nestaTela();
  const novo = !params.item && params.veiculo !== undefined;
  const corpo = h('div', { class: 'secao' }, carregando());
  const titulo = h('span', null, novo ? 'Novo item' : 'Item de manutenção');
  let voltar = novo ? `/veiculos/${params.veiculo}/manutencao` : '/manutencao';
  const pagina = casca({ titulo, voltar, conteudo: [contaGravavel() ? null : faixaDaConta(), corpo] });
  const linkVoltar = pagina.querySelector('.topo a[aria-label="Voltar"]');

  (async () => {
    const { frota, itens, leituras, hoje } = await dadosDaFrota();
    const item = novo ? null : itens.find((i) => i.id === params.item) ?? (await manutencao.obter(params.item));
    if (!novo && !item) {
      trocar(corpo, vazio({ simbolo: 'chave', titulo: 'Item não encontrado', texto: 'Ele pode ter sido excluído.', acao: h('a', { class: 'botao', href: '#/manutencao' }, 'Ver a manutenção') }));
      return;
    }
    const v = frota.find((x) => x.id === (item?.veiculo_id ?? params.veiculo));
    if (!v) {
      trocar(corpo, vazio({ titulo: 'Caminhão não encontrado', acao: h('a', { class: 'botao', href: '#/veiculos' }, 'Ver veículos') }));
      return;
    }
    voltar = `/veiculos/${v.id}/manutencao`;
    linkVoltar?.setAttribute('href', `#${voltar}`);
    titulo.textContent = novo ? `Novo item · ${textoCaminhao(v)}` : item.nome;
    const gravavel = podeGravar() && v.ativo;
    const doVeiculo = itens.filter((i) => i.veiculo_id === v.id);
    const faltam = faltamNoPlano(doVeiculo);
    const atual = kmAtual(v, leituras, doVeiculo);
    const situacao = item ? planoDoVeiculo(v, { itens, leituras, hoje }).find((p) => p.item.id === item.id)?.situacao : null;

    const campoNome = campo({
      rotulo: 'Nome do item', nome: 'nome', obrigatorio: true, valor: item?.nome ?? '', atributos: { maxlength: '80', autocomplete: 'off' },
      dica: novo ? 'Por exemplo: "Correia do alternador", "Arrefecimento".' : null,
    });
    const campoEscolha = novo && campo({
      rotulo: 'Item', nome: 'codigo', tipo: 'select', obrigatorio: true, valor: faltam[0]?.codigo ?? 'outro',
      opcoes: [...faltam.map((c) => [c.codigo, c.nome]), ['outro', 'Outro (escrever o nome)']],
      aoMudar: () => aoEscolher(),
    });
    const campoKm = campo({
      rotulo: 'A cada (km)', nome: 'intervalo_km', formato: 'km', valor: item?.intervalo_km != null ? numeroBR(item.intervalo_km) : '',
      atributos: { inputmode: 'numeric' }, dica: 'Deixe em branco se o item vence só pelo tempo.',
    });
    const campoMeses = campo({
      rotulo: 'A cada (meses)', nome: 'intervalo_meses', formato: 'dia', valor: item?.intervalo_meses != null ? String(item.intervalo_meses) : '',
      atributos: { inputmode: 'numeric', maxlength: '3' }, dica: 'Deixe em branco se o item vence só pelo km.',
    });

    const valorDe = (nome) => form.querySelector(`[name="${nome}"]`)?.value ?? '';
    function aoEscolher() {
      const escolhido = CATALOGO.find((c) => c.codigo === valorDe('codigo'));
      campoNome.hidden = Boolean(escolhido);
      form.querySelector('[name="nome"]').value = escolhido?.nome ?? '';
      form.querySelector('[name="intervalo_km"]').value = escolhido?.km ? numeroBR(escolhido.km) : '';
      form.querySelector('[name="intervalo_meses"]').value = escolhido?.meses ? String(escolhido.meses) : '';
    }

    const form = formulario({
      filhos: [
        campoEscolha,
        campoNome,
        h('div', { class: 'grade-2' }, campoKm, campoMeses),
        h('fieldset', { class: 'campo' },
          h('legend', null, 'Última vez feita'),
          h('div', { class: 'grade-2' },
            campo({ rotulo: 'Data', nome: 'base_data', tipo: 'date', valor: item ? item.base_data ?? '' : hoje }),
            campo({
              rotulo: 'Km no painel', nome: 'base_km', formato: 'km',
              valor: item ? (item.base_km != null ? numeroBR(item.base_km) : '') : (atual ? numeroBR(atual.km) : ''),
              atributos: { inputmode: 'numeric' },
            })),
          h('p', { class: 'dica' }, 'Se não souber, deixe a data de hoje e o km atual: o app começa a contar daqui.')),
        item && (item.feita_data || item.feita_km != null) && h('p', { class: 'pequeno' },
          `Pelas despesas: ${[item.feita_data && data(item.feita_data), item.feita_km != null && `${numeroBR(item.feita_km)} km`].filter(Boolean).join(' · ')}. Vale a mais recente.`),
        campo({ rotulo: 'Observação', nome: 'observacao', tipo: 'textarea', valor: item?.observacao ?? '' }),
      ],
      textoBotao: novo ? 'Incluir no plano' : 'Salvar alterações',
      somenteLeitura: !gravavel,
      validar: (d) => {
        const erros = {};
        const nome = (d.nome ?? '').trim();
        if (!(nome.length >= 2 && nome.length <= 80)) erros.nome = 'Escreva o nome do item.';
        const km = d.intervalo_km ? lerInteiro(d.intervalo_km) : null;
        const meses = d.intervalo_meses ? lerInteiro(d.intervalo_meses) : null;
        if (d.intervalo_km && !(km >= 100 && km <= 2000000)) erros.intervalo_km = 'De 100 a 2.000.000 km.';
        if (d.intervalo_meses && !(meses >= 1 && meses <= 120)) erros.intervalo_meses = 'De 1 a 120 meses.';
        if (!d.intervalo_km && !d.intervalo_meses) erros.intervalo_km = 'Informe de quantos em quantos km ou meses (ou os dois).';
        if (d.base_km && !(lerInteiro(d.base_km) >= 0)) erros.base_km = 'Só números, sem vírgula.';
        if (d.base_data && d.base_data > hoje) erros.base_data = 'A última vez não pode ser depois de hoje.';
        return erros;
      },
      aoEnviar: async (d) => {
        const dados = {
          nome: d.nome.trim(),
          intervalo_km: d.intervalo_km ? lerInteiro(d.intervalo_km) : null,
          intervalo_meses: d.intervalo_meses ? lerInteiro(d.intervalo_meses) : null,
          base_data: d.base_data || null,
          base_km: d.base_km ? lerInteiro(d.base_km) : null,
          observacao: d.observacao || null,
        };
        if (novo) {
          const codigo = CATALOGO.some((c) => c.codigo === d.codigo) ? d.codigo : null;
          await manutencao.criar({ ...dados, veiculo_id: v.id, codigo });
          avisar('Item incluído no plano.');
        } else {
          await manutencao.atualizar(item.id, dados, item);
          avisar('Alterações salvas.');
        }
        tela.ir(voltar, { substituir: true });
      },
      extras: !novo && gravavel && h('button', {
        class: 'botao texto',
        type: 'button',
        onClick: async () => {
          const ok = await confirmar({
            titulo: 'Tirar este item do plano?',
            texto: 'Ele deixa de avisar. As despesas em que ele foi marcado continuam como estão.',
            sim: 'Tirar do plano',
            perigo: true,
          });
          if (!ok) return;
          try {
            await manutencao.excluir(item.id);
            avisar('Item tirado do plano.');
            tela.ir(voltar, { substituir: true });
          } catch (erro) {
            avisar(mensagemDeErro(erro), 'erro');
          }
        },
      }, 'Tirar do plano'),
    });

    trocar(corpo,
      situacao && faixa([textoDoIntervalo(item), ' · ', textoDaUltima(situacao.ultima), situacao.estado !== 'sem_base' ? ` · ${textoDaSituacao(situacao)}` : ''],
        situacao.estado === 'vencida' ? 'perigo' : situacao.estado === 'ok' ? 'info' : 'alerta'),
      form);
    if (novo) aoEscolher();
  })().catch((erro) => {
    console.error(erro);
    trocar(corpo, faixa(mensagemDeErro(erro), 'perigo'));
  });
  return pagina;
}

// ---------------------------------------------------------------------
// Na despesa: marcar os itens feitos
// ---------------------------------------------------------------------
// Os itens do plano do caminhão, para marcar. `aoMudar` avisa a tela (o km
// passa a ser obrigatório quando um item conta por km).
export function blocoManutencaoFeita({ marcados = [], aoMudar = null } = {}) {
  const lista = h('div', { class: 'caixas' });
  const bloco = h('fieldset', { class: 'campo manutencao-feita', hidden: true },
    h('legend', null, 'Manutenção preventiva feita'),
    lista,
    h('p', { class: 'dica' }, 'Marque o que foi feito: o plano do caminhão volta a contar daqui (com o km do painel).'));
  let itensAtuais = [];
  const escolhidos = new Set(marcados);
  return {
    bloco,
    // Mostra os itens deste caminhão (e os já marcados nesta despesa).
    mostrar(itens, visivel) {
      itensAtuais = itens;
      bloco.hidden = !visivel || itens.length === 0;
      trocar(lista, itens.map((i) => h('label', { class: 'caixa' },
        h('input', {
          type: 'checkbox',
          checked: escolhidos.has(i.id),
          dataset: { manutencaoItem: i.id },
          onChange: (e) => {
            if (e.currentTarget.checked) escolhidos.add(i.id);
            else escolhidos.delete(i.id);
            aoMudar?.();
          },
        }),
        h('span', null, i.nome, h('small', null, ` · ${textoDoIntervalo(i).replace(/^A cada/, 'a cada')}`)))));
    },
    // Os marcados que ainda são do caminhão escolhido (e visíveis).
    marcados() {
      if (bloco.hidden) return [];
      return itensAtuais.filter((i) => escolhidos.has(i.id)).map((i) => i.id);
    },
    pedeKm() {
      return itensAtuais.some((i) => escolhidos.has(i.id) && i.intervalo_km) && !bloco.hidden;
    },
  };
}
